import { useState } from 'react';
import {
  CONFERENCE_COST_PLAN_KEYS,
  CONFERENCE_REVENUE_PLAN_KEYS,
  DEFAULT_PLANS,
  PLAN_LABELS,
} from '../lib/cashflow-defaults';
import type {
  CashflowPlanKey,
  EventData,
  PaymentMilestoneDTO,
} from '../lib/types';

type Props = {
  data: EventData;
  // Template-specific key lists; defaults to the conference layout for
  // backward compatibility.
  revenueKeys?: CashflowPlanKey[];
  costKeys?: CashflowPlanKey[];
  onChange: (
    plans: NonNullable<EventData['cashflowPlans']>,
  ) => void;
};

export default function CashflowSettings({
  data,
  revenueKeys = CONFERENCE_REVENUE_PLAN_KEYS,
  costKeys = CONFERENCE_COST_PLAN_KEYS,
  onChange,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const overrides = data.cashflowPlans ?? {};
  const overrideCount = Object.keys(overrides).length;

  function setPlanFor(key: CashflowPlanKey, plan: PaymentMilestoneDTO[]) {
    onChange({ ...overrides, [key]: plan });
  }

  function resetPlanFor(key: CashflowPlanKey) {
    const next = { ...overrides };
    delete next[key];
    onChange(next);
  }

  function resetAll() {
    onChange({});
  }

  return (
    <div className="card cashflow-settings-card">
      <div className="section-head">
        <h2>Payment timing</h2>
        <div className="row center">
          {overrideCount > 0 && (
            <span className="muted">
              {overrideCount} customised
            </span>
          )}
          {overrideCount > 0 && (
            <button className="btn ghost sm" onClick={resetAll}>
              Reset all
            </button>
          )}
          <button className="btn ghost sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? '▴ Collapse' : '▾ Customize'}
          </button>
        </div>
      </div>

      {!expanded && (
        <div className="muted fs-13">
          Defaults: venue 50% T-180d / 50% T-30d, sponsors land T-60d, tickets
          spread 20/30/50% across T-180d / T-60d / T-14d, etc. Click{' '}
          <strong>Customize</strong> to fit your contracts.
        </div>
      )}

      {expanded && (
        <div className="cf-settings-body">
          <PlanGroup
            label="Revenue"
            keys={revenueKeys}
            overrides={overrides}
            onChange={setPlanFor}
            onReset={resetPlanFor}
          />
          <PlanGroup
            label="Costs"
            keys={costKeys}
            overrides={overrides}
            onChange={setPlanFor}
            onReset={resetPlanFor}
          />
        </div>
      )}
    </div>
  );
}

function PlanGroup({
  label,
  keys,
  overrides,
  onChange,
  onReset,
}: {
  label: string;
  keys: CashflowPlanKey[];
  overrides: NonNullable<EventData['cashflowPlans']>;
  onChange: (key: CashflowPlanKey, plan: PaymentMilestoneDTO[]) => void;
  onReset: (key: CashflowPlanKey) => void;
}) {
  return (
    <div className="cf-settings-group">
      <div className="cf-settings-group-label">{label}</div>
      <div className="cf-plan-rows">
        {keys.map((k) => (
          <PlanRow
            key={k}
            planKey={k}
            override={overrides[k]}
            onChange={(plan) => onChange(k, plan)}
            onReset={() => onReset(k)}
          />
        ))}
      </div>
    </div>
  );
}

function PlanRow({
  planKey,
  override,
  onChange,
  onReset,
}: {
  planKey: CashflowPlanKey;
  override: PaymentMilestoneDTO[] | undefined;
  onChange: (plan: PaymentMilestoneDTO[]) => void;
  onReset: () => void;
}) {
  const isCustom = !!override;
  const plan = override ?? DEFAULT_PLANS[planKey];

  function setDays(idx: number, days: number) {
    onChange(plan.map((m, i) => (i === idx ? { ...m, daysBeforeEvent: days } : { ...m })));
  }
  function setPctPercent(idx: number, pctPercent: number) {
    const fraction = clamp(pctPercent, 0, 200) / 100;
    onChange(plan.map((m, i) => (i === idx ? { ...m, pct: fraction } : { ...m })));
  }
  function addMilestone() {
    const remaining = Math.max(0, 1 - plan.reduce((s, m) => s + m.pct, 0));
    onChange([...plan, { pct: remaining, daysBeforeEvent: 30 }]);
  }
  function removeMilestone(idx: number) {
    if (plan.length <= 1) return; // require at least one
    onChange(plan.filter((_, i) => i !== idx));
  }

  // Sum check — render-time, with a small tolerance so 0.33+0.33+0.34 reads
  // as 100% even with float math.
  const sumPct = plan.reduce((s, m) => s + m.pct, 0);
  const sumDisplay = Math.round(sumPct * 1000) / 10; // one decimal
  const sumOk = Math.abs(sumPct - 1) < 0.005;

  return (
    <div className={`cf-plan-row ${isCustom ? 'is-custom' : ''}`}>
      <div className="cf-plan-label">{PLAN_LABELS[planKey]}</div>
      <div className="cf-plan-milestones">
        {plan.map((m, idx) => (
          <span key={idx} className="cf-plan-milestone">
            <input
              type="number"
              className="input cf-plan-pct-input"
              value={Math.round(m.pct * 100 * 10) / 10}
              onChange={(e) => setPctPercent(idx, Number(e.target.value))}
              min={0}
              max={200}
              step={1}
              aria-label={`${PLAN_LABELS[planKey]} milestone ${idx + 1} percent`}
            />
            <span className="cf-plan-at">%</span>
            <span className="cf-plan-at">at T−</span>
            <input
              type="number"
              className="input cf-plan-days"
              value={m.daysBeforeEvent}
              onChange={(e) => setDays(idx, Math.round(Number(e.target.value)))}
              min={-365}
              max={1095}
              step={1}
              aria-label={`${PLAN_LABELS[planKey]} milestone ${idx + 1} days before event`}
            />
            <span className="cf-plan-at">d</span>
            <button
              type="button"
              className="cf-plan-remove"
              onClick={() => removeMilestone(idx)}
              disabled={plan.length <= 1}
              title={plan.length <= 1 ? 'At least one milestone is required' : 'Remove milestone'}
            >
              ✕
            </button>
          </span>
        ))}

        <button type="button" className="btn ghost sm cf-plan-add" onClick={addMilestone}>
          + Add milestone
        </button>

        <span className={`cf-plan-sum ${sumOk ? 'ok' : 'warn'}`}>
          Sum: {sumDisplay}%
          {sumOk ? ' ✓' : ' (should be 100%)'}
        </span>
      </div>
      <div className="cf-plan-actions">
        {isCustom ? (
          <button className="btn ghost sm" onClick={onReset}>Reset</button>
        ) : (
          <span className="muted cf-plan-default">default</span>
        )}
      </div>
    </div>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}
